import { z } from 'zod';
import config from '@packages/config/src';
import {
  fieldMessageGeneral,
  fieldMessagesInvalid,
  fieldMessagesRequired,
} from './AddressValidation.messages';

export const FieldErrorReason = {
  required: 'Required', // Required (with big R) to avoid confusion with zod's required_error
  invalid: 'Invalid',
  streetPP: 'StreetPackstationPostfiliale',
};

export const getFieldError = (reason: string, fieldName: string) => {
  if (reason === FieldErrorReason.required) {
    return fieldMessagesRequired[fieldName as keyof typeof fieldMessagesRequired];
  }
  if (reason === FieldErrorReason.invalid) {
    return fieldMessagesInvalid[fieldName as keyof typeof fieldMessagesInvalid];
  }
  if (reason === FieldErrorReason.streetPP) {
    return fieldMessagesInvalid.streetPackOrPost;
  }
  return fieldMessageGeneral.somethingWrong;
};

const nameRegex = /^[A-Za-zÄÖÜäöü][A-Za-z'.\-\sßÄÖÜäöü]*$/;

export const phoneNumberCharacterPattern = /^[0-9()+\-/ ]*$/;

export const addressValidationSchema = (markFieldsMandatory: boolean) =>
  z.object({
    gender: markFieldsMandatory
      ? z
          .string({ required_error: FieldErrorReason.required })
          .min(1, { message: FieldErrorReason.required })
      : z.string().optional(),
    firstName: markFieldsMandatory
      ? z
          .string()
          .min(1, { message: FieldErrorReason.required })
          .regex(nameRegex, { message: FieldErrorReason.invalid })
      : z.string().optional(),
    lastName: markFieldsMandatory
      ? z
          .string()
          .min(1, { message: FieldErrorReason.required })
          .regex(nameRegex, { message: FieldErrorReason.invalid })
      : z.string().optional(),
    street: markFieldsMandatory
      ? z
          .string()
          .min(1, { message: FieldErrorReason.required })
          .regex(/^[A-Za-z.\-/ßÄÖÜäöü 0-9]{0,30}$/, { message: FieldErrorReason.invalid })
          .regex(/^(?!.*(packstation|postfiliale)).*$/i, { message: FieldErrorReason.streetPP })
      : z.string().optional(),
    houseNumber: markFieldsMandatory
      ? z
          .string()
          .min(1, { message: FieldErrorReason.required })
          .regex(/^[1-9]([a-z0-9.,;/ +-]*[a-z0-9])?\s*$/, { message: FieldErrorReason.invalid })
      : z.string().optional(),
    additional: z.string().optional(),
    zipCode: markFieldsMandatory
      ? z
          .string()
          .min(1, { message: FieldErrorReason.required })
          .regex(new RegExp(`^[0-9]{${config.order.addressValidation.zipCode.length}}$`), {
            message: FieldErrorReason.invalid,
          })
      : z.string().optional(),
    city: markFieldsMandatory
      ? z
          .string()
          .min(1, { message: FieldErrorReason.required })
          .regex(/^[A-Za-z.\-ßÄÖÜäöü 0-9]{0,30}$/, { message: FieldErrorReason.invalid })
      : z.string().optional(),
    primaryPhoneNumber: z
      .string()
      .regex(phoneNumberCharacterPattern, { message: FieldErrorReason.invalid })
      .optional()
      .or(z.literal('')),
  });
